import type { AxiosError } from 'axios';
import type { ApiErrorResponse, ApiFieldError } from '@shared/types/api';

/**
 * Normalized application error. Every failed request is translated into this
 * shape so UI code never has to reason about raw Axios internals.
 *
 * ## The backend error contract
 *
 * Every service answers errors through one GlobalExceptionFilter
 * (`packages/common/src/http/global-exception.filter.ts`):
 *
 *     { success: false, message: string, errors: string[] }
 *
 * `errors` is a list of **strings**, not objects. For a class-validator failure
 * each entry is `"<property.path> <constraint>"` — e.g.
 * `"email must be an email"` or `"items.0.amount must not be less than 0"` —
 * and `message` is the single failure or `"N validation errors, first: …"`.
 * Other errors put the same text in `message` and `errors`.
 *
 * `fieldErrors` derives a field → message map from those strings so a form can
 * place each one under its input. It is a best-effort projection: an entry
 * whose first word is not a field name (a business-rule message) simply never
 * matches a form field and stays in the summary.
 */
export class ApiError extends Error {
  readonly status: number;
  /** Raw backend messages (`errors: string[]`). */
  readonly messages: string[];
  /** Field-level errors derived from `messages` (see class note). */
  readonly errors: ApiFieldError[];
  readonly isNetworkError: boolean;

  constructor(message: string, status: number, messages: string[] = [], isNetworkError = false) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.messages = messages;
    this.errors = toFieldErrors(messages);
    this.isNetworkError = isNetworkError;
  }

  /** Authentication problem — the session is missing, expired or revoked. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /**
   * Authenticated, but the backend refused the action (permission, application
   * access or company scope). Never a session problem: refreshing or logging
   * out would be wrong.
   */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isConflict(): boolean {
    return this.status === 409;
  }

  get isValidation(): boolean {
    return this.status === 400 || this.status === 422;
  }

  get isRateLimited(): boolean {
    return this.status === 429;
  }

  get isServerError(): boolean {
    return this.status >= 500;
  }

  /** First field error for a form field name (dot paths supported). */
  fieldError(field: string): string | undefined {
    return this.errors.find((entry) => entry.field === field)?.message;
  }

  static fromAxiosError(error: AxiosError<ApiErrorResponse>): ApiError {
    if (error.response) {
      const { status, data } = error.response;
      // Only the JSON envelope carries a message; a proxy's HTML page does not.
      const serverMessage = typeof data?.message === 'string' ? data.message : undefined;
      const messages = normalizeMessages(data?.errors);
      return new ApiError(safeMessage(status, serverMessage, error.message), status, messages);
    }

    if (error.code === 'ERR_CANCELED') {
      return new ApiError('Request cancelled', 0);
    }

    if (error.request) {
      return new ApiError(
        'Unable to reach the server. Check your connection and try again.',
        0,
        [],
        true,
      );
    }

    return new ApiError(error.message ?? 'Unexpected error', 0);
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * User-facing message for a status.
 *
 * The backend's filter turns any unexpected failure into a 500 with a fixed
 * text, so a 500/502/504 never shows server wording. A 501 or 503 from the
 * backend carries a message written for the user ("… is not implemented
 * yet"), shown as is; the same statuses from a proxy arrive as HTML, which is
 * never shown.
 */
function safeMessage(status: number, serverMessage: string | undefined, transportMessage: string): string {
  const readable = serverMessage && !serverMessage.trimStart().startsWith('<') ? serverMessage : undefined;
  if (status === 429) return 'Too many requests. Please wait a moment and try again.';
  if (status === 503) return readable ?? 'The service is temporarily unavailable. Please try again shortly.';
  if (status === 501) return readable ?? 'This feature is not available yet.';
  if (status >= 500) return 'Something went wrong on our side. Please try again.';
  return readable ?? (transportMessage || 'Request failed');
}

function normalizeMessages(errors: unknown): string[] {
  if (!Array.isArray(errors)) return [];
  return errors.flatMap((entry) => {
    if (typeof entry === 'string') return [entry];
    // Defensive: an object entry `{ message }` from an older contract.
    if (entry && typeof entry === 'object' && 'message' in entry) {
      const text = (entry as { message?: unknown }).message;
      return typeof text === 'string' ? [text] : [];
    }
    return [];
  });
}

/** Property paths in class-validator messages: `email`, `items.0.amount`, `address.city`. */
const FIELD_PATH = /^([a-zA-Z_][\w]*(?:\.[\w]+)*)\s+(.+)$/;

function toFieldErrors(messages: readonly string[]): ApiFieldError[] {
  return messages.flatMap((message) => {
    const match = FIELD_PATH.exec(message);
    if (!match?.[1]) return [];
    // "property X should not exist" names the field second.
    const unknownProperty = /^property (\S+) should not exist$/.exec(message);
    if (unknownProperty?.[1]) return [{ field: unknownProperty[1], message }];
    return [{ field: match[1], message }];
  });
}
